import React, { useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, Button, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import { apiError } from '../format';
import { PharmacyOption } from '../types';
import { rulesApi } from './rulesApi';
import { AUTOCOMPLETE_SLOT_PROPS, BORDER, DARK, FIELD_SX, KIND_LABELS, KIND_ORDER, MUTED, PRIMARY_BTN_SX, SKIP_REASONS, SOFT, fmtRuleValue } from './rulesConstants';
import { EvalChip, ModeChip, Panel, PanelHeader, Segmented } from './RuleBits';
import { DocKind, RulesOverview, SimulateResponse } from './rulesTypes';
import { notify } from '../../../notifications';

interface Props {
  overview: RulesOverview;
}

const byRegionName = (a: PharmacyOption, b: PharmacyOption) => (a.region || '~').localeCompare(b.region || '~', 'ru') || a.name.localeCompare(b.name, 'ru', { numeric: true });

const VERDICT_STYLE = {
  error: { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  warning: { color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  success: { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' }
};

/** Вкладка «Проверка»: «что будет, если аптека A создаст такой документ?» - без создания документов и записи в журнал */
const RuleSimulatorTab: React.FC<Props> = ({ overview }) => {
  const { catalog, senders, recipients } = overview;
  const [kind, setKind] = useState<DocKind>('direct');
  const [from, setFrom] = useState<PharmacyOption | null>(null);
  const [to, setTo] = useState<PharmacyOption | null>(null);
  const [positions, setPositions] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<SimulateResponse | null>(null);

  const sortedSenders = useMemo(() => [...senders].sort(byRegionName), [senders]);
  const sortedRecipients = useMemo(() => [...recipients].sort(byRegionName), [recipients]);
  const defOf = (code: string) => catalog.rules.find(r => r.code === code);

  const run = async () => {
    if (!from) return;
    setLoading(true);
    try {
      setRes(await rulesApi.simulate({
        kind, from_guid: from.guid, to_guid: to?.guid,
        positions: positions === '' ? undefined : positions, amount: amount === '' ? undefined : amount.replace(',', '.')
      }));
    } catch (err: any) {
      setRes(null);
      notify.error(apiError(err, 'Не удалось выполнить проверку'));
    } finally {
      setLoading(false);
    }
  };

  const pharmacyField = (label: string, val: PharmacyOption | null, set: (v: PharmacyOption | null) => void, options: PharmacyOption[]) => (
    <Autocomplete
      size="small" options={options} value={val} onChange={(_, v) => { set(v); setRes(null); }}
      getOptionLabel={o => o.name} isOptionEqualToValue={(a, b) => a.guid === b.guid}
      groupBy={o => o.region || 'Без региона'} noOptionsText="Ничего не найдено"
      renderInput={params => <TextField {...params} label={label} sx={FIELD_SX} />}
      slotProps={AUTOCOMPLETE_SLOT_PROPS}
      sx={{ flex: '1 1 260px' }}
    />
  );

  const result = res?.result;
  const verdict = !result ? null
    : result.blocked ? { kind: 'error' as const, title: 'Документ не будет создан', text: 'Нарушены правила в режиме «Блокирует»' }
      : result.observed.length ? { kind: 'warning' as const, title: 'Документ будет создан, но с замечаниями', text: 'Нарушения попадут в журнал (режим «Наблюдение»)' }
        : { kind: 'success' as const, title: 'Документ будет создан', text: 'Нарушений нет' };
  const vs = verdict ? VERDICT_STYLE[verdict.kind] : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Panel>
        <PanelHeader
          title="Проверить сценарий"
          subtitle="Ничего не создаётся и в журнал не пишется. Лимит направления считается по реальным документам; сумма и позиции - по введённым значениям (пустые поля не проверяются)."
        />
        <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: MUTED, mb: 0.75 }}>Вид документа</Typography>
            <Segmented
              value={kind} onChange={k => { setKind(k); setRes(null); }}
              options={KIND_ORDER.map(k => ({ value: k, label: KIND_LABELS[k] }))}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {pharmacyField('Аптека-отправитель', from, setFrom, sortedSenders)}
            {pharmacyField('Аптека-получатель', to, setTo, sortedRecipients)}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              size="small" type="number" label="Позиций в документе" value={positions} onChange={e => setPositions(e.target.value)}
              inputProps={{ min: 0, step: 1 }} sx={{ width: 220, ...FIELD_SX }}
              InputProps={{ endAdornment: <InputAdornment position="end"><Box component="span" sx={{ fontSize: 12.5, color: MUTED }}>поз.</Box></InputAdornment> }}
            />
            <TextField
              size="small" type="number" label="Сумма документа" value={amount} onChange={e => setAmount(e.target.value)}
              inputProps={{ min: 0, step: 100 }} sx={{ width: 220, ...FIELD_SX }}
              InputProps={{ endAdornment: <InputAdornment position="end"><Box component="span" sx={{ fontSize: 12.5, color: MUTED }}>₸</Box></InputAdornment> }}
            />
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained" disableElevation onClick={run} disabled={!from || loading} sx={{ ...PRIMARY_BTN_SX, height: 40, px: 2.5 }}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              Проверить
            </Button>
          </Box>
        </Box>
      </Panel>

      {result && verdict && vs && (
        <>
          <Box sx={{ px: 2.5, py: 1.5, borderRadius: '12px', bgcolor: vs.bg, border: `1px solid ${vs.border}`, borderLeft: `4px solid ${vs.color}` }}>
            <Typography sx={{ fontWeight: 700, color: vs.color, fontSize: 15, lineHeight: 1.35 }}>{verdict.title}</Typography>
            <Typography sx={{ fontSize: 13, color: vs.color, opacity: 0.85, mt: 0.25 }}>{verdict.text}</Typography>
          </Box>

          <Panel>
            <PanelHeader title="Результат по правилам" />
            {result.results.map((r, i) => {
              const def = defOf(r.ruleCode);
              return (
                <Box
                  key={r.ruleCode}
                  sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', borderTop: i ? '1px solid #f1f5f9' : 'none' }}
                >
                  <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: DARK }}>{r.name}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                      {r.status === 'not_set' || r.status === 'off'
                        ? (r.status === 'off' ? (r.level === 'exception' ? 'Для этой аптеки правило не применяется (исключение)' : 'Правило выключено') : 'Правило не задано')
                        : `${r.level === 'exception' ? 'Исключение' : 'Правило сети'}: ${r.text || fmtRuleValue(def, r.value ?? 0, r.period)}`}
                      {r.status === 'skipped' && r.reason ? ` · не проверено: ${SKIP_REASONS[r.reason] || r.reason}` : ''}
                    </Typography>
                    {r.status === 'violated' && r.message && <Typography sx={{ fontSize: 12.5, color: '#b91c1c', mt: 0.25 }}>{r.message}</Typography>}
                  </Box>
                  {r.mode && r.mode !== 'off' && <ModeChip mode={r.mode} />}
                  <EvalChip status={r.status} />
                </Box>
              );
            })}
          </Panel>

          {res && res.effective.length > 0 && (
            <Panel>
              <PanelHeader
                title="Что увидит фармацевт при создании документа"
                subtitle="Ограничения показываются сразу при начале документа, а не после его заполнения."
              />
              <Box sx={{ px: 2.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {res.effective.map(e => (
                  <Box
                    key={`${e.ruleCode}-${e.recordId}`}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 1.5, py: 1, borderRadius: '8px', bgcolor: SOFT, border: `1px solid ${BORDER}` }}
                  >
                    <Typography sx={{ fontSize: 13.5, color: DARK }}>{e.text}</Typography>
                    {typeof e.used === 'number' && typeof e.remaining === 'number' && (
                      <Typography sx={{ fontSize: 12.5, color: MUTED }}>(использовано {e.used}, осталось {e.remaining})</Typography>
                    )}
                    {e.mode === 'observe' && <ModeChip mode="observe" />}
                  </Box>
                ))}
              </Box>
            </Panel>
          )}

          {result.warnings.length > 0 && (
            <Alert severity="info" sx={{ borderRadius: '8px', '& .MuiAlert-message': { fontSize: 13 } }}>{result.warnings.join('; ')}</Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default RuleSimulatorTab;
